/*!
 * Copyright (c) Trimble Inc. All rights reserved
 */

import { Component, OnInit, Input, ViewChild, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, FormArray } from '@angular/forms';
import { FittingSizeManagementService } from '../../sharedServices/fitting-size-management.service';
import { DialogBoxComponent } from '../../shared/dialog-box/dialog-box.component';
import { CustomSnackBarComponent } from '../../shared/customSnackBar/customSnackBar.component';
import { Subscription } from 'rxjs';
import { PartialySavedFittings } from '../../models/PartialySavedFittings';
import { IFittingSizeComponent } from '../../models/interfaces/IFittingSizeComponent';
import { FittingSizeDataService } from '../../sharedServices/fittingSize-data.service';
import { formatNumber } from '@angular/common';
import { validateStartPage } from '../../models/validators/start-page.validator';
import { ActionTypeEnumDto, PipeFittingSizeDto } from 'api/models';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatMenuTrigger } from '@angular/material/menu';
import { MatSnackBar } from '@angular/material/snack-bar';

@Component({
  selector: 'app-piping-plumbing-fitting-size',
  templateUrl: './piping-plumbing.component.html',
  styleUrls: ['./piping-plumbing.component.css']
})
export class PipingPlumbingComponent
  implements OnInit, OnDestroy, IFittingSizeComponent {
  columnHeader;
  objectKeys = Object.keys;
  refreshSubscription: Subscription;
  isLoadingSubscription: Subscription;
  partialySavedFittingsSubscription: Subscription;
  addedFromExistingFittingSizeSubscription: Subscription;
  form: FormGroup;
  nominalSizeList: any[];
  hasOnlySavedFittings = true;
  isLoadingResults: boolean;
  selectedElement: FormGroup;
  comment: string;
  disabledColumns = new Array<string>();
  contextMenuPosition = { x: '0px', y: '0px' };
  @ViewChild(MatMenuTrigger) contextMenu: MatMenuTrigger;

  @Input() partialSaveActionType: ActionTypeEnumDto;
  @Input() sendForAprovalActionType: ActionTypeEnumDto;
  @Input() deleteActionType: ActionTypeEnumDto;
  @Input() showAddFittingButton = true;
  @Input() showButtons = true;

  constructor(
    public dialog: MatDialog,
    private readonly snackBar: MatSnackBar,
    private readonly sharedService: FittingSizeManagementService,
    private readonly formBuilder: FormBuilder,
    private readonly fittingSizeDataService: FittingSizeDataService
  ) { }

  ngOnInit(): void {
    this.loadSubscriptions();
    this.columnHeader = this.sharedService.getTableHeader();
    this.refresh();
  }

  ngOnDestroy(): void {
    if (this.refreshSubscription) {
      this.refreshSubscription.unsubscribe();
    }
    if (this.partialySavedFittingsSubscription) {
      this.partialySavedFittingsSubscription.unsubscribe();
    }
    if (this.addedFromExistingFittingSizeSubscription) {
      this.addedFromExistingFittingSizeSubscription.unsubscribe();
    }
    this.resetArrayForm([]);
  }

  private loadSubscriptions() {
    this.refreshSubscription = this.sharedService.refreshNeeded.subscribe(
      () => {
        this.refresh();
        this.columnHeader = this.sharedService.getTableHeader();
      }
    );

    this.partialySavedFittingsSubscription = this.sharedService.getPartialySavedFittings.subscribe(
      value => {
        this.setNominalSizesAndAddedFittings(value);
      }
    );

    this.addedFromExistingFittingSizeSubscription = this.sharedService.getAddedFromExistingFittings.subscribe(
      () => {
        this.addExistingFieldValue();
      }
    );
  }

  refresh() {
    this.setDisabledColumns();
    this.resetArrayForm([]);
    this.sharedService.setTableDataList(null);
    this.getNominalSizesAndAddedFittings();
  }

  get newFittingSizes(): FormArray {
    return this.form.get('newFittingSizes') as FormArray;
  }

  hasFittingSelected(): boolean {
    return this.sharedService.hasSelectedFitting();
  }

  isFittingSentForApprove(): boolean {
    return this.sharedService.hasFittingBeenSentToApprove();
  }

  onWeightValueChange(element: FormGroup) {
    if (this.sharedService.selectedFitting.unitID === 1) {
      element.controls.weightMetric.setValue(
        formatNumber(element.controls.weightImperial.value / 2.2046, 'en')
      );
    }
    if (this.sharedService.selectedFitting.unitID === 3) {
      element.controls.weightImperial.setValue(
        formatNumber(element.controls.weightMetric.value * 2.2046, 'en')
      );
    }

    this.onStartPageValueChange(element);
  }

  onStartPageValueChange(element: FormGroup) {
    element.controls.startPage.setValidators(
      validateStartPage.createValidator(element.controls.startPage)
    );
    element.controls.startPage.updateValueAndValidity();
  }

  onSizeChange(elementForm: FormGroup) {
    if (this.checkSizeExists(elementForm)) {
      this.openSnackBar('Selected Size already exists');
      elementForm.reset();
      return;
    }
    this.setDefaultValues(elementForm);
  }

  getSharedService() {
    return this.sharedService;
  }

  getNominalSizesAndAddedFittings() {
    this.sharedService.getNominalSizesAndAddedFittings();
  }

  setNominalSizesAndAddedFittings(value: PartialySavedFittings) {
    this.nominalSizeList = value.NominalSizeList;
    value.PartialySavedFittings
      ? this.resetArrayForm(value.PartialySavedFittings.controls)
      : this.resetArrayForm([]);
  }

  addFieldValue() {
    const newItem = {} as PipeFittingSizeDto;
    this.newFittingSizes.push(this.sharedService.getMappedFormGroup(newItem));
    this.resetArrayForm(this.newFittingSizes.controls);
  }

  addExistingFieldValue() {
    if (
      this.updateSizeAlreadyExists(
        this.sharedService.selectedExistingFittingSize.bdid
      ) === false
    ) {
      this.newFittingSizes.push(
        this.sharedService.getMappedFormGroupWithExistingValues(
          this.nominalSizeList,
          this.sharedService.selectedExistingFittingSize
        )
      );
    }
    if (this.partialSaveActionType === ActionTypeEnumDto.PartialSaveDelete) {
      this.newFittingSizes.disable();
    }

    this.resetArrayForm(this.newFittingSizes.controls);
  }

  removeFieldValue(comment: any) {
    this.isLoadingResults = true;
    const indexToDelete = this.newFittingSizes.controls.findIndex(
      element => element === this.selectedElement
    );
    this.newFittingSizes.removeAt(indexToDelete);
    this.resetArrayForm(this.newFittingSizes.controls);

    if (this.selectedElement.controls.isPartialySaved.value) {
      this.sharedService.removeSavedRecord(
        this.selectedElement.controls.id.value,
        this.deleteActionType, comment
      );
    }
    this.isLoadingResults = false;

  }

  onPartialSaveClick() {
    const dialogRef = this.dialog.open(DialogBoxComponent, {
      data: {
        title: 'Partial Save Information',
        message: 'Are you sure do you want to Partial Save the added Fittings ?'
      }
    });
    this.handlePartialSaveDialogButtons(dialogRef);
  }

  onRemoveSavedRecordClick() {
    const dialogRef = this.dialog.open(DialogBoxComponent, {
      data: {
        title: 'Remove record',
        message:
          'Are you sure do you want to remove this parial saved record ?',
        addComment: true,
        comment: ''
      }
    });
    this.handleRemoveSavedRecord(dialogRef);
  }

  handleRemoveSavedRecord(dialogRef: MatDialogRef<DialogBoxComponent, any>) {
    const subscribeOk = dialogRef.componentInstance.okClickEvent.subscribe(
      () => {
        if (dialogRef.componentInstance.data.comment !== '') {
          this.removeFieldValue(dialogRef.componentInstance.data.comment);

        } else {
          this.openSnackBar('Add a comment before deleting the partial saved record');
        }
      }
    );

    dialogRef.afterClosed().subscribe(() => {
      subscribeOk.unsubscribe();
    });
  }

  onSendForAprovalClick() {
    const dialogRef = this.dialog.open(DialogBoxComponent, {
      data: {
        title: 'Send for Approval Information',
        message:
          'Are you sure do you want to Send For Approval? All added fittings will be automaticaly saved.',
        addComment: true,
        comment: ''
      }
    });
    this.handleSendForAprovalDialogButtons(dialogRef);
  }

  handlePartialSaveDialogButtons(
    dialogRef: MatDialogRef<DialogBoxComponent, any>
  ) {
    const subscribeOk = dialogRef.componentInstance.okClickEvent.subscribe(
      () => {
        if (this.partialSaveActionType === ActionTypeEnumDto.PartialSaveDelete) {
          this.sharedService.prepareNewAddedFittings(
            this.newFittingSizes,
            this.partialSaveActionType
          );
          return;
        }
        if (this.form.valid) {
          this.sharedService.prepareNewAddedFittings(
            this.newFittingSizes,
            this.partialSaveActionType
          );
        } else {
          this.showInvalidControls();
          this.openSnackBar('Please complete all required fields');
          return;
        }
      }
    );
    dialogRef.afterClosed().subscribe(() => {
      subscribeOk.unsubscribe();
    });
  }

  handleSendForAprovalDialogButtons(
    dialogRef: MatDialogRef<DialogBoxComponent, any>
  ) {
    const subscribeOk = dialogRef.componentInstance.okClickEvent.subscribe(
      () => {
        if (
          this.form.valid &&
          dialogRef.componentInstance.data.comment !== ''
        ) {
          this.sharedService.executeSendForAproval(
            this.sendForAprovalActionType,
            dialogRef.componentInstance.data.comment
          );
        } else {
          this.openSnackBar('Please add a comment before sending for approval');
        }
      }
    );
    dialogRef.afterClosed().subscribe(() => {
      subscribeOk.unsubscribe();
    });
  }

  editSelectedField() {
    const objectKeys = Object.keys(this.selectedElement.controls);
    this.selectedElement.controls.isPartialySaved.setValue(false);
    objectKeys.forEach(key => {
      if (!this.disabledColumns.includes(key)) {
        this.selectedElement.controls[key].enable();
      }
    });
  }

  showInvalidControls() {
    this.newFittingSizes.controls.forEach(item => {
      item.markAllAsTouched();
    });
  }

  openSnackBar(message: string) {
    this.snackBar.openFromComponent(CustomSnackBarComponent, {
      duration: 3000,
      data: message
    });
  }

  isPartialSaveDisabled(): boolean {
    return this.hasFittingSelected() &&
      (this.newFittingSizes.length === 0) ||
      this.isFittingSentForApprove()
      ? true
      : false;
  }

  isSendForApprovalDisabled(): boolean {
    return this.hasElementsNotPartialySaved(this.newFittingSizes) ||
      this.hasFittingSelected() &&
      (this.newFittingSizes.length === 0 ||
        this.sharedService.isFittingDisabled() ||
        this.isFittingSentForApprove())
      ? true
      : false;
  }

  hasElementsNotPartialySaved(fittingsArray: FormArray): boolean {
    const array = fittingsArray.controls as FormGroup[];
    return array.some(code => code.controls.isPartialySaved.value === null || code.controls.isPartialySaved.value === false);
  }

  isAddNewSizeDisabled() {
    return this.hasFittingSelected() && this.isFittingSentForApprove()
      ? true
      : false;
  }

  onContextMenuClick(mouseEvent: MouseEvent, element: any) {
    mouseEvent.preventDefault();
    this.contextMenuPosition.x = mouseEvent.clientX + 'px';
    this.contextMenuPosition.y = mouseEvent.clientY + 'px';
    this.selectedElement = element;
    this.contextMenu.openMenu();
  }

  setDefaultValues(elementForm: FormGroup) {
    this.sharedService.setDefaultValues(
      elementForm,
      elementForm.controls.nominalSize.value
    );
  }

  checkSizeExists(elementForm: FormGroup) {
    return this.sizeAlreadyExists(
      elementForm.controls.nominalSize.value.sizeID
    );
  }

  sizeAlreadyExists(selectedId: number): boolean {
    if (
      this.fittingSizeDataService
        .getTableData()
        .filter(element => element.sizeID === selectedId).length > 0
    ) {
      return true;
    }
    const fittingsList = this.newFittingSizes.controls as FormGroup[];
    const existingFitttingSizes = new Array<any>();

    fittingsList.forEach(element => {
      if (
        element.controls.nominalSize.value &&
        element.controls.nominalSize.value.sizeID === selectedId
      ) {
        existingFitttingSizes.push(element);
      }
    });
    return existingFitttingSizes.length >= 2 ? true : false;
  }

  updateSizeAlreadyExists(bdid: string): boolean {
    let isExisting = false;
    this.newFittingSizes.controls.forEach(element => {
      if (element.get('bdid').value === bdid) {
        isExisting = true;
      }
    });
    return isExisting;
  }

  resetArrayForm(controlsArray) {
    this.form = this.formBuilder.group({
      newFittingSizes: this.formBuilder.array(controlsArray)
    });
  }

  setDisabledColumns() {
    if (
      this.sharedService.getSelectedFitting().unitID === 1 &&
      this.sharedService.getActionPage() === 'Update'
    ) {
      this.disabledColumns = [
        'nominalSize',
        'BDID',
        'DecSize1',
        'DecSize2',
        'DecSize3',
        'DecSize4',
        'DecSize5',
        'DecSize6',
        'WeightMetric'
      ];
    } else if (this.sharedService.getSelectedFitting().unitID === 1) {
      this.disabledColumns = [
        'BDID',
        'DecSize1',
        'DecSize2',
        'DecSize3',
        'DecSize4',
        'DecSize5',
        'DecSize6',
        'WeightMetric'
      ];
    } else {
      this.disabledColumns = [
        'BDID',
        'DecSize1',
        'DecSize2',
        'DecSize3',
        'DecSize4',
        'DecSize5',
        'DecSize6',
        'WeightImperial'
      ];
    }
  }
}
